import React, {
  useEffect, useRef, useState, useCallback,
} from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import classNames from 'classnames';
import { AutoSizer, List } from 'react-virtualized';
import { useOnClickOutside } from '../hooks/hooks';

const SearchBox = ({
  input,
  meta,
  name,
  selectedItem,
  onChange,
  onSearch,
  labelField,
  loading,
  items,
  renderItem,
  renderItemText,
  renderDropdown,
  iconClassName,
  iconLeft,
  alwaysOpen,
  closeOnSelect,
  rowHeight,
  onOpen,
  onBlur: onBlurCallback,
  dropdownOnly,
  noPadding,
  children,
  ...props
}) => {
  const [focus, setFocus] = useState(false);
  const [dropdownHeight, setDropdownHeight] = useState(false);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const onBlur = useCallback(() => {
    setFocus(false);
    if (onBlurCallback) {
      onBlurCallback();
    }
  }, [onBlurCallback]);
  useOnClickOutside(onBlur, [inputRef, dropdownRef]);

  const onScroll = useCallback(() => {
    if (!inputRef.current || !dropdownRef.current) {
      return;
    }

    const { innerHeight } = window;
    const {
      left,
      width,
      bottom: top,
    } = inputRef.current.getBoundingClientRect();
    const { style } = dropdownRef.current;
    style.top = `${top + 4}px`;
    style.left = `${left - 1}px`;
    style.width = `${width + 2}px`;
    const maxHeight = Math.max(
      Math.min(
        Math.min((items.length || 1) * rowHeight + 10, 600),
        innerHeight - top,
      ),
      0,
    );
    style.maxHeight = `${maxHeight - 8}px`;
    setDropdownHeight(maxHeight - 10);
  }, [items.length, rowHeight]);

  const onDropdownRef = (ref) => {
    dropdownRef.current = ref;
    onScroll();
  };

  useEffect(() => {
    document.body.addEventListener('scroll', onScroll);
    window.addEventListener('resize', onScroll);
    window.addEventListener('orientationchange', onScroll);
    onScroll();

    return () => {
      document.body.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      window.removeEventListener('orientationchange', onScroll);
    };
  }, [onScroll]);

  const defaultRenderItemText = renderItemText || ((item) => item[labelField]);

  const defaultRenderItem = renderItem
    || ((item, index, props) => (
      <DropdownItem {...props}>{defaultRenderItemText(item)}</DropdownItem>
    ));

  const DropdownItemWrapper = ({
    key, style, index,
  }) => (
    <div key={key} style={style}>
      {defaultRenderItem(items[index], index, {
        height: `${rowHeight}px`,
        onClick: (e) => {
          e.preventDefault();
          e.stopPropagation();

          if (closeOnSelect) {
            onBlur();
          }
          onChange(items[index]);
        },
        onKeyPress: (e) => e.key === 'Enter' && onChange(items[index]),
        tabIndex: '0',
        role: 'button',
        active: selectedItem === items[index],
      })}
    </div>
  );

  const renderDropdownContents = () => (
    <AutoSizer style={{ height: dropdownHeight }}>
      {({ width }) => (
        <List
          width={width}
          height={dropdownHeight}
          rowHeight={rowHeight}
          rowCount={items.length}
          rowRenderer={({ key, style, index }) => (
            <DropdownItemWrapper key={key} style={style} index={index} />
          )}
        />
      )}
    </AutoSizer>
  );

  const renderDropdownContentsIE = () => (
    items.map((_, index) => (
      <DropdownItemWrapper style={{}} index={index} />
    ))
  );

  let dropdownContents = (renderDropdown && renderDropdown(renderDropdownContents))
    || renderDropdownContents();

  const isIE = window.document.documentMode;
  if (isIE) {
    dropdownContents = (renderDropdown && renderDropdownContentsIE(renderDropdownContents))
    || renderDropdownContentsIE();
  }

  const open = (alwaysOpen || focus) && !!dropdownContents;

  useEffect(() => {
    if (onOpen) {
      onOpen(open);
    }
  }, [onOpen, open]);

  return (
    <Search noPadding={noPadding} open={open}>
      <SearchLabel dropdownOnly={dropdownOnly} htmlFor={input.name || name}>
        {dropdownOnly ? (
          <DropdownButton
            bold={selectedItem}
            noPadding={noPadding}
            {...props}
            ref={inputRef}
            id={input.name || name}
            name={input.name || name}
            onFocus={() => setFocus(true)}
            onClick={() => setFocus(true)}
          >
            {children}
          </DropdownButton>
        ) : (
          <SearchInput
            bold={selectedItem}
            autoComplete="off"
            type="text"
            {...props}
            ref={inputRef}
            id={input.name || name}
            name={input.name || name}
            onChange={({ target: { value } }) => {
              if (onSearch) {
                onSearch(value);
                setFocus(true);
              }
            }}
            onFocus={() => setFocus(true)}
            onClick={() => setFocus(true)}
            isFocus={focus}
            iconClassName={iconClassName}
            iconLeft={iconLeft}
          />
        )}
        {iconClassName && (
          <SearchIcon iconLeft={iconLeft} loading={loading}>
            {(loading && <i className=" fas fa-spin fa-spinner" />) || (
              <i className={classNames(iconClassName)} />
            )}
          </SearchIcon>
        )}
      </SearchLabel>
      {open && (
        <DropdownWrapper focus={focus} ref={onDropdownRef}>
          <Dropdown>{dropdownContents}</Dropdown>
        </DropdownWrapper>
      )}
      <input value={selectedItem} {...props} {...input} type="hidden" />
      {meta && meta.touched && meta.error && (
        <InputError>{meta.error}</InputError>
      )}
    </Search>
  );
};

SearchBox.propTypes = {
  input: PropTypes.shape({}),
  name: PropTypes.string,
  selectedItem: PropTypes.shape({}),
  bold: PropTypes.bool,
  loading: PropTypes.bool,
  items: PropTypes.array,
  renderItem: PropTypes.func,
  renderItemText: PropTypes.func,
  renderDropdown: PropTypes.func,
  labelField: PropTypes.string,
  iconClassName: PropTypes.string,
  iconLeft: PropTypes.bool,
  alwaysOpen: PropTypes.bool,
  closeOnSelect: PropTypes.bool,
  rowHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.func]).isRequired,
  onOpen: PropTypes.func,
  onBlur: PropTypes.func,
  dropdownOnly: PropTypes.bool,
  noPadding: PropTypes.bool,
  children: PropTypes.node,
};

SearchBox.defaultProps = {
  input: {},
  name: null,
  selectedItem: null,
  loading: false,
  bold: false,
  items: null,
  renderItem: null,
  renderItemText: null,
  renderDropdown: null,
  labelField: 'text',
  iconClassName: 'fa fa-search',
  iconLeft: false,
  alwaysOpen: false,
  closeOnSelect: false,
  onOpen: null,
  onBlur: null,
  dropdownOnly: null,
  noPadding: false,
  children: null,
};

const SearchLabel = styled.label`
  position: relative;
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  ${({ dropdownOnly }) => (dropdownOnly
  ? css`
          cursor: pointer;
        `
  : css`
          cursor: text;
        `)}
`;

const Search = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;

  ${({ noPadding }) => !noPadding
    && css`
      padding: 10px 0;
    `}
`;

const SearchIcon = styled.div`
  position: absolute;
  top: 50%;
  display: inline;
  height: 100%;
  margin: auto 0;
  color: #999;
  line-height: 0;
  pointer-events: none;

  i {
    line-height: 0;
  }

  ${({ iconLeft }) => (iconLeft
      && css`
        left: 18px;
      `)
    || css`
      right: 18px;
    `}

  ${({ loading }) => loading
    && css`
      i {
        color: #000;
      }
    `}
`;

const DropdownButton = styled.div`
  flex: 1 0 auto;
  border: none;
  font-weight: 400;
  line-height: 1.8;
  border-radius: 8px;
  overflow: hidden;
  transition: background-color 0.1s ease-out;

  ${({ noPadding }) => !noPadding
    && css`
      padding: 10px 20px;
    `}

  ${({ bold }) => bold
    && css`
      font-weight: 700;
    `}
`;

const SearchInput = styled.input`
  flex: 1 0 auto;
  background-color: #dededc;
  border: none;
  padding: 10px 20px;
  font-weight: 400;
  line-height: 1.8;
  border: 1px solid #dbd9d7;
  border-radius: 8px;
  overflow: hidden;
  transition: background-color 0.1s ease-out;

  ${({ iconClassName, iconLeft }) => iconClassName
    && ((iconLeft
      && css`
        padding-left: 46px;
      `)
      || css`
        padding-right: 46px;
      `)}

  ${({ isFocus }) => isFocus
    && css`
      background-color: #fff;

      &::placeholder {
        color: #6a6868;
      }
    `}

  ${({ bold }) => bold
    && css`
      font-weight: 700;
    `}

  ::placeholder {
    color: #949393;
  }
`;

const InputError = styled.div`
  color: #c22;
  text-align: center;
`;

const DropdownWrapper = styled.div`
  position: fixed;
  display: flex;
  margin: 0;
  border: 1px solid #dbd9d7;
  border-radius: 8px;
  z-index: 100;
  transition: background-color 0.1s ease-out;
  overflow: hidden;

  ${({ focus }) => focus
    && css`
      background-color: #fff;
    `}
`;

const Dropdown = styled.div`
  display: block;
  flex: 1 1 auto;
  flex-direction: column;
`;

export const DropdownItem = styled.div`
  position: relative;
  display: flex;
  flex: 1 1 auto;
  align-items: center;
  margin: auto 0;
  padding: 0 24px;
  height: ${({ height }) => height};
  line-height: 1;
  white-space: nowrap;
  text-overflow: ellipsis;

  ${({ onClick }) => onClick
    && css`
      cursor: pointer;

      :hover {
        background-color: #eee;
      }
    `}

  ${({ active }) => active
    && css`
      font-weight: 700;
    `}

  :last-child {
    border-bottom: none;
  }
`;

export default SearchBox;
